import { Phone, Check } from 'lucide-react';

import { Btn } from '@/Components/Btn';
import { Headshot } from '@/Components/Headshot';
import { Pill } from '@/Components/Pill';
import { Stars } from '@/Components/Stars';
import type { LawyerCardData } from '@/Types/lawyer';

/**
 * Vertical lawyer tile. Used on the homepage "Top rated near you" rail and
 * inside state/PA grid pages. Hero gradient + Headshot, Featured / Bar
 * verified pills, name + firm + city/state, Stars, practice-area pills,
 * meta line, Call + Profile buttons.
 *
 *   <LawyerCardV data={lawyer} />
 */

export interface LawyerCardVProps {
    data: LawyerCardData;
    className?: string;
}

export function LawyerCardV({ data, className }: LawyerCardVProps) {
    const cls = [
        'flex flex-col bg-card border border-rule rounded-[14px] overflow-hidden',
        className ?? '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <article className={cls} aria-label={`${data.name} — ${data.firm}`}>
            <div className="relative h-[170px] bg-[linear-gradient(180deg,#EDE3D3_0%,#E2D5C0_100%)]">
                <div className="absolute inset-0 grid place-items-center">
                    <Headshot
                        initials={data.initials}
                        hue={data.hue}
                        size={130}
                        radius={12}
                    />
                </div>
                {data.featured === true && (
                    <div className="absolute top-3 left-3">
                        <Pill tone="ember">Featured</Pill>
                    </div>
                )}
                <div className="absolute top-3 right-3">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium tracking-wide uppercase rounded-full bg-card/90 backdrop-blur-sm">
                        <Check
                            size={12}
                            className="text-sage"
                            aria-hidden="true"
                        />
                        <span className="text-ink-soft">Bar verified</span>
                    </span>
                </div>
            </div>
            <div className="flex flex-col gap-2.5 p-4.5 flex-1">
                <div>
                    <h3 className="text-[20px] leading-tight mb-0.5">
                        {data.name}
                    </h3>
                    <div className="text-[13px] text-ink-soft">
                        {data.firm} · {data.city}, {data.state}
                    </div>
                </div>
                <Stars value={data.rating} reviews={data.reviews} />
                <div className="flex flex-wrap gap-1.5">
                    {data.areas.slice(0, 3).map((a) => (
                        <Pill key={a} tone="muted">
                            {a}
                        </Pill>
                    ))}
                </div>
                <div className="flex gap-3.5 text-[12px] text-ink-mute mt-0.5">
                    <span>
                        <span className="text-ink font-medium">
                            {data.years}
                        </span>{' '}
                        yrs licensed
                    </span>
                    <span>·</span>
                    <span>Free consult</span>
                    {data.langs?.includes('ES') === true && (
                        <>
                            <span>·</span>
                            <span>EN · ES</span>
                        </>
                    )}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-auto pt-3">
                    <Btn
                        kind="secondary"
                        size="sm"
                        icon={<Phone size={14} aria-hidden="true" />}
                    >
                        Call
                    </Btn>
                    <Btn kind="primary" size="sm">
                        Profile
                    </Btn>
                </div>
            </div>
        </article>
    );
}

export default LawyerCardV;
